import axios from 'axios';
import settings from 'settings';
import * as helpers from 'library/helpers';
import * as cookies from 'library/cookies';
import { ApiEndpoints } from 'api/urls';

const ignoreTokenForEndpoints = [ApiEndpoints.tokenAuth];
const ignoreTenancyForEndpoints = [ApiEndpoints.tokenAuth, ApiEndpoints.whoami];

class JwtService {
  constructor(config) {
    this.CONSTANTS = {
      JWT_AUTH_TOKEN: 'JWT_AUTH_TOKEN',
      JWT_USER_DATA: 'JWT_USER_DATA'
    };

    this.user = null;

    if (config) {
      this.config = config;
      this.refreshThreshold = config.refreshThreshold || 10;
      this.tokenRefreshInProgress = false;
    }
    else {
      throw new Error('JWT config parameters not specified');
    }
  }

  static getTenancies(tenancies) {
    return tenancies.map(t => ({
      tenancyId: t.tenancy.id,
      tenancyName: t.tenancy.name,
      description: t.tenancy.description,
      timezone: t.tenancy.default_timezone,
      adGroup: t.tenancy.active_directory_group,
      canViewOthersRoster: t.tenancy.roster_view_can_select_user || false,
      optimiserState: t.tenancy.optimiser_state,
      otPresets: t.tenancy.ot_presets
    }));
  }

  /**
   * @description Get user tenancy permissions based on tenancy
   * @param {array} tenancyPermissions - list of tenancy permissions
   * @param {object} tenancy - tenancy object
   * @return {array} tenancy permissions
   */
  static getCurrentTenancyPermissions(tenancyPermissions = [], tenancy = {}) {
    const tenancyId = tenancy ? tenancy.tenancyId : -1;
    const found = tenancyPermissions.find(item => item.tenancy_id === tenancyId);
    return (found ? found.permissions.map(item => item.name) : []);
  }

  static createUser(userData) {
    const user = userData || {};
    const { id } = userData;
    const email = user.email || '';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const tenancies = JwtService.getTenancies(user.tenancies);
    const tenancyPermissions = user.tenancy_permissions || [];
    const currentTenancy = (tenancies.length === 1 ? tenancies[0] : null);
    const currentTenancyPermissions = JwtService.getCurrentTenancyPermissions(tenancyPermissions, currentTenancy) || [];

    return {
      id,
      userName: email,
      displayName: `${firstName} ${lastName}`,
      firstName,
      lastName,
      tenancies,
      tenancyPermissions,
      currentTenancy,
      currentTenancyPermissions
    };
  }

  login(email, password) {
    const self = this;
    const params = {
      email,
      password
    };

    return new Promise((resolve, reject) => {
      axios.post(ApiEndpoints.tokenAuth, params)
        .then(
          (response) => {
            const auth = JSON.parse(JSON.stringify(response.data));
            if (auth.token) {
              // ------ Login Success. Fetch token ------
              self.setCacheItem(self.CONSTANTS.JWT_AUTH_TOKEN, auth.token);
              resolve(auth.token);
            }
            else {
              // ------ Login Success. Invalid token ------
              self.removeCacheItem(self.CONSTANTS.JWT_AUTH_TOKEN);
              reject(Error('Unable to retrieve JWT token'));
            }
          },
          (error) => {
            // ------ Login Failed! ------
            reject(error);
          }
        );
    });
  }

  logout() {
    this.removeCacheItem(this.CONSTANTS.JWT_AUTH_TOKEN);
    this.removeCacheItem(this.CONSTANTS.JWT_USER_DATA);
    this.user = null;
    this.config = null;
  }

  isAuthenticated() {
    const token = this.getCachedToken();
    return token !== null && token.length > 0;
  }

  acquireToken() {
    const self = this;

    return new Promise((resolve, reject) => {
      const cachedToken = self.getCachedToken();

      if (cachedToken) {
        const epochNow = Math.round(new Date().getTime() / 1000);
        const tokenObject = JSON.parse(atob(cachedToken.split('.')[1]));
        const tokenExpiry = (tokenObject.exp - epochNow) / 60; // in minutes

        if (tokenExpiry < self.refreshThreshold && !self.tokenRefreshInProgress) {
          // ------ Refresh Token. Token expiry is < 15min threshold. ------
          self.refreshToken(cachedToken)
            .then(
              (response) => {
                const token = response;
                resolve(token);
              },
              (error) => {
                reject(error);
              }
            );
        }
        else {
          // ------ Return Token. Token expiry is > 15min. ------
          resolve(cachedToken);
        }
      }
      else {
        reject(Error('JWT Token not found'));
      }
    });
  }

  refreshToken(token) {
    const self = this;
    const params = { token };

    // -- Refresh the token and update the local storage
    // -- with the new token.
    // ---
    this.tokenRefreshInProgress = true;
    return axios.post(ApiEndpoints.tokenRefresh, params)
      .then(
        (response) => {
          const user = response.data.toJS() || {};
          self.tokenRefreshInProgress = false;

          // ------ Ensure token exists, update the token cache and return then token ------
          if (user.token) {
            this.setCacheItem(this.CONSTANTS.JWT_AUTH_TOKEN, user.token);
          }
          else {
            this.removeCacheItem(this.CONSTANTS.JWT_AUTH_TOKEN);
          }
          return user.token;
        },
        (error) => {
          // ------ Clear token cache and return the error ------
          self.tokenRefreshInProgress = false;
          this.removeCacheItem(this.CONSTANTS.JWT_AUTH_TOKEN);
          throw error;
        }
      );
  }

  getAuthHeader(requestUrl) {
    const self = this;
    const endPoint = helpers.getApiPathName(requestUrl);

    return new Promise((resolve, reject) => {
      // ------ Fetch the user tenancy details ------
      let tenancyId = null;
      let ignoreTenancy = true;

      if (!ignoreTenancyForEndpoints.includes(endPoint)) {
        const cachedUser = self.getCachedUser();
        tenancyId = cachedUser && cachedUser.currentTenancy ? cachedUser.currentTenancy.tenancyId : null;
        ignoreTenancy = false;
      }

      // ------ Fetch the token ------
      if (!ignoreTokenForEndpoints.includes(endPoint)) {
        this.acquireToken()
          .then(
            (response) => {
              const token = response;
              if (token) {
                resolve({
                  tenancyId,
                  ignoreTenancy,
                  token,
                  ignoreToken: false,
                  authorization: `JWT ${token}`
                });
              }
              else {
                reject(Error('JWT Token is null'));
              }
            },
            (error) => {
              reject(error);
            }
          );
      }
      else {
        // ------ Token is not required. Return null ------
        resolve({
          tenancyId,
          ignoreTenancy,
          token: null,
          ignoreToken: true,
          authorization: null
        });
      }
    });
  }

  getCachedToken() {
    const token = this.getCacheItem(this.CONSTANTS.JWT_AUTH_TOKEN);

    if (token) {
      const epochNow = Math.round(new Date().getTime() / 1000);
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const tokenExpiry = tokenPayload.exp - epochNow; // in seconds

      if (tokenExpiry > 0) {
        // -- Token is valid.
        // -- Return Token
        // ---
        return token;
      }
      // -- Token has expired.
      // -- Clear cache and return null
      // ---
      this.removeCacheItem(this.CONSTANTS.JWT_AUTH_TOKEN);
      return null;
    }
    return null;
  }

  getCachedUser() {
    if (this.user) {
      return this.user;
    }

    // -- User data is empty. Check the cache to see if we can fetch
    // -- the user data from there. If the cache data is empty, logout
    // -- and redirect user to login. We need the user data to correctly
    // -- setup the user's tenancy access and permissions.
    // ---
    const userData = JSON.parse(this.getCacheItem(this.CONSTANTS.JWT_USER_DATA));
    if (userData) {
      this.user = userData;
    }
    return this.user;
  }

  getUser() {
    // ------ Fetch the user's details ------
    return axios.get(ApiEndpoints.whoami)
      .then(
        (response) => {
          const userData = JSON.parse(JSON.stringify(response.data));
          this.user = JwtService.createUser(userData);

          this.setCacheItem(this.CONSTANTS.JWT_USER_DATA, JSON.stringify(this.user));
          return this.user;
        },
        (error) => {
          this.user = null;
          this.removeCacheItem(this.CONSTANTS.JWT_USER_DATA);
          throw error;
        }
      );
  }

  setCurrentTenancy(tenancy) {
    const cachedUser = this.getCachedUser();
    if (cachedUser) {
      cachedUser.currentTenancy = tenancy;
      cachedUser.currentTenancyPermissions = JwtService.getCurrentTenancyPermissions(cachedUser.tenancyPermissions, tenancy) || [];
      this.setCacheItem(this.CONSTANTS.JWT_USER_DATA, JSON.stringify(cachedUser));
    }
  }

  // -- FUNCTION: getCacheItem
  // -- Searches the value for the given key in the cache

  // ---
  getCacheItem = function (key) {
    if (this.config && this.config.cacheLocation) {
      // Use localStorage
      if (this.config.cacheLocation === 'localStorage') {
        if (!this.supportsLocalStorage()) {
          return null;
        }
        return localStorage.getItem(key);
      }

      // Use cookieStorage
      if (this.config.cacheLocation === 'cookieStorage') {
        return cookies.getCookie(key);
      }
    }

    // Default as sessionStorage
    if (!this.supportsSessionStorage()) {
      return null;
    }
    return sessionStorage.getItem(key);
  };

  // -- FUNCTION: setCacheItem
  // -- Saves the key-value pair in the cache
  // ---
  setCacheItem(key, obj) {
    if (this.config && this.config.cacheLocation) {
      // Use localStorage
      if (this.config.cacheLocation === 'localStorage') {
        if (!this.supportsLocalStorage()) {
          return false;
        }
        localStorage.setItem(key, obj);
        return true;
      }

      // Use cookieStorage
      if (this.config.cacheLocation === 'cookieStorage') {
        cookies.setCookie(key, obj);
        return true;
      }
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      return false;
    }

    sessionStorage.setItem(key, obj);
    return true;
  }

  // -- FUNCTION: removeCacheItem
  // -- Saves the key-value pair in the cache
  // ---
  removeCacheItem(key) {
    if (this.config && this.config.cacheLocation) {
      // Use localStorage
      if (this.config.cacheLocation === 'localStorage') {
        if (!this.supportsLocalStorage()) {
          return false;
        }
        localStorage.removeItem(key);
        return true;
      }

      // Use cookieStorage
      if (this.config.cacheLocation === 'cookieStorage') {
        cookies.removeCookie(key);
        return true;
      }
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      return false;
    }

    sessionStorage.removeItem(key);
    return true;
  }

  // -- FUNCTION: supportsLocalStorage
  // -- Returns true if browser supports localStorage, false otherwise.
  // ---
  supportsLocalStorage = function () {
    try {
      if (!window.localStorage) return false; // Test availability
      window.localStorage.setItem('storageTest', 'A'); // Try write
      if (window.localStorage.getItem('storageTest') !== 'A') return false; // Test read/write
      window.localStorage.removeItem('storageTest'); // Try delete
      if (window.localStorage.getItem('storageTest')) return false; // Test delete
      return true; // Success
    }
    catch (e) {
      return false;
    }
  };

  // -- FUNCTION: supportsSessionStorage
  // -- Returns true if browser supports sessionStorage, false otherwise.
  // ---
  supportsSessionStorage = function () {
    try {
      if (!window.sessionStorage) return false; // Test availability
      window.sessionStorage.setItem('storageTest', 'A'); // Try write
      if (window.sessionStorage.getItem('storageTest') !== 'A') return false; // Test read/write
      window.sessionStorage.removeItem('storageTest'); // Try delete
      if (window.sessionStorage.getItem('storageTest')) return false; // Test delete
      return true; // Success
    }
    catch (e) {
      return false;
    }
  };
}

export default new JwtService(settings.JWT_CONFIG);
